import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Camera, Award } from 'lucide-react';

const CHAPTERS = [
  {
    num: '01',
    title: 'The Geometry of Patience',
    testId: 'manifesto-chapter-1',
    body: 'I wait for light as others wait for revelation. Architecture teaches us that silence is structural — that the most powerful compositions exist in the negative space between decisions. Every frame is a decade of practice distilled into a single held breath. I do not capture moments. I earn them.',
  },
  {
    num: '02',
    title: 'The Unvarnished Eye',
    testId: 'manifesto-chapter-2',
    body: 'Truth is not found in the expected angle. I circle a subject — human or stone — until it reveals its contradiction. The worn column beside pristine glass. The exhausted elegance in a model\'s resting face. These frictions are the only honest photographs. Everything else is decoration.',
  },
  {
    num: '03',
    title: 'Light as Material',
    testId: 'manifesto-chapter-3',
    body: 'In architecture, light is the fifth element. In portraiture, it is the soul made visible. I have spent years learning to read its grammar — the 4pm oblique in Lisbon, the diffused pallor of winter Paris, the harsh midday blade of Oaxacan sun. Light is not something to manage. It is something to listen to.',
  },
];

const ABOUT_IMG = 'https://images.unsplash.com/photo-1536180838057-b604200e6f36?w=700&q=85&auto=format&fit=crop';

function Chapter({ ch, isOpen, onToggle }) {
  return (
    <div data-testid={ch.testId} className="border-b border-black/[0.08]">
      <button
        onClick={onToggle}
        className="w-full flex items-start justify-between gap-4 py-6 text-left group"
      >
        <div className="flex items-start gap-5">
          <span className="text-[11px] font-mono text-[#B88349] tracking-widest mt-1 flex-shrink-0">{ch.num}</span>
          <span className="font-serif text-xl sm:text-2xl font-light text-[#121212] leading-snug group-hover:text-neutral-600 transition-colors duration-300">
            {ch.title}
          </span>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="flex-shrink-0 mt-1"
        >
          <ChevronDown size={18} strokeWidth={1.25} className="text-neutral-400" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="pl-10 pb-6 text-base text-neutral-500 font-light leading-relaxed max-w-xl">
              {ch.body}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AboutManifesto() {
  const [openIdx, setOpenIdx] = useState(0);

  const toggle = (i) => setOpenIdx(openIdx === i ? -1 : i);

  return (
    <section id="manifesto" className="py-24 md:py-32 bg-[#F5F3EC]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Manifesto header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <p className="text-[11px] font-mono uppercase tracking-[0.28em] text-neutral-400 mb-2">The Manifesto</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-[#121212]">
            On Making Photographs
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-16 lg:gap-20">
          {/* Left: Chapters */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            {CHAPTERS.map((ch, i) => (
              <Chapter key={ch.num} ch={ch} isOpen={openIdx === i} onToggle={() => toggle(i)} />
            ))}
          </motion.div>

          {/* Right: Bio Card */}
          <motion.div
            id="about"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-black/[0.04]">
              <img
                src={ABOUT_IMG}
                alt="Elena Vance"
                className="w-full h-56 object-cover object-top"
              />
              <div data-testid="about-bio-text" className="p-7">
                <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#B88349] mb-1">Photographer</p>
                <h3 className="font-serif text-2xl font-light text-[#121212] mb-1">Elena Vance</h3>
                <p className="text-xs font-mono text-neutral-400 mb-4">Paris · Kyoto · The world</p>
                <p className="text-sm text-neutral-500 font-light leading-relaxed mb-6">
                  Editorial and architectural photographer working at the intersection of minimalism,
                  documentary truth, and the poetry of inhabited space. Client list spans fashion,
                  luxury goods, and fine art publishing.
                </p>

                {/* Gear */}
                <div className="flex items-start gap-2 mb-4">
                  <Camera size={14} strokeWidth={1.25} className="text-neutral-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-400 mb-1">Equipment</p>
                    <p className="text-xs text-[#121212] font-light">Leica M11 · Hasselblad 907X</p>
                    <p className="text-xs text-neutral-500 font-light">50mm Summilux · 45mm · 35mm APO</p>
                  </div>
                </div>

                {/* Awards */}
                <div className="flex items-start gap-2">
                  <Award size={14} strokeWidth={1.25} className="text-neutral-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-400 mb-1">Recognition</p>
                    <p className="text-xs text-[#121212] font-light">Rencontres d'Arles '25</p>
                    <p className="text-xs text-neutral-500 font-light">Tokyo Photo Award · Aperture Foundation</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
